import React from 'react';
import Modal from './Modal';
import CopyButton from './CopyButton';

// A loader a több elemű mezőket '|' jellel fűzi össze (lezáró | is van),
// az üres eredményt pedig DISK_UNKNOWN / MAC_UNKNOWN jelöli.
function splitList(value) {
  if (!value) return [];
  return String(value).split('|').map(v => v.trim()).filter(Boolean);
}

function isUnknown(value) {
  return !value || /^[A-Z_]+_UNKNOWN$/.test(String(value).trim());
}

function Row({ label, value, list = false }) {
  const items = list ? splitList(value) : [];
  const missing = isUnknown(value);

  return (
    <div className="py-3 border-b border-white/[0.05] last:border-0">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">{label}</span>
        {!missing && <CopyButton value={value} variant="icon" />}
      </div>

      {missing ? (
        <span className="text-sm text-gray-700 italic">
          {value ? `${value} — not readable on that machine` : 'Not reported yet'}
        </span>
      ) : list ? (
        <div className="space-y-1">
          {items.map((item, i) => (
            <div key={`${item}-${i}`} className="font-mono text-sm text-gray-200 break-all">
              {item}
            </div>
          ))}
          {items.length > 1 && (
            <span className="text-xs text-gray-600">{items.length} entries</span>
          )}
        </div>
      ) : (
        <div className="font-mono text-sm text-gray-200 break-all">{value}</div>
      )}
    </div>
  );
}

function SectionLabel({ children, hint }) {
  return (
    <div className="pt-4 pb-1 first:pt-0">
      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{children}</span>
      {hint && <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{hint}</p>}
    </div>
  );
}

// Melyik horgonykészletre futott a loader hashe. Lásd HWIDGenerator.h.
const TIER_HINT = {
  A: 'SMBIOS UUID + baseboard serial + CPU — the strongest set.',
  B: 'Only one of the two board anchors was usable, so the system disk is in the hash too.',
  C: 'The machine reports no usable SMBIOS data — the hash falls back to CPU, disks and the Windows install.',
};

export default function HwidInfoModal({ user, onClose }) {
  const lastSeen = user.hwid_last_seen
    ? user.hwid_last_seen.slice(0, 16).replace('T', ' ')
    : null;
  const reboundAt = user.hwid_rebound_at
    ? user.hwid_rebound_at.slice(0, 16).replace('T', ' ')
    : null;
  const tier = user.hwid_tier || null;

  return (
    <Modal
      size="lg"
      title="HWID Info"
      subtitle={`Hardware components reported by ${user.username}'s loader`}
      onClose={onClose}
      icon={
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
             strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <rect x="9" y="9" width="6" height="6" rx="1" />
          <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
        </svg>
      }
      footer={
        <div className="flex justify-end">
          <button onClick={onClose} className="btn-secondary px-4 py-2 text-sm">Close</button>
        </div>
      }
    >
      {lastSeen ? (
        <p className="text-xs text-gray-500 mb-4">
          Last reported: <span className="text-gray-300 font-mono">{lastSeen}</span>
        </p>
      ) : (
        <div className="surface-raised text-sm text-gray-400 px-4 py-3 mb-4 leading-relaxed">
          This account has never signed in through the loader, so no hardware details
          were collected yet.
        </div>
      )}

      {(tier || reboundAt) && (
        <div className="surface-raised px-4 py-3 mb-4 space-y-1.5">
          {tier && (
            <p className="text-xs text-gray-500 leading-relaxed">
              Anchor set: <span className="text-gray-200 font-mono">tier {tier}</span>
              {TIER_HINT[tier] && <span className="text-gray-600"> — {TIER_HINT[tier]}</span>}
            </p>
          )}
          {reboundAt && (
            <p className="text-xs text-gray-500 leading-relaxed">
              Auto-rebound <span className="text-gray-200">{user.hwid_rebind_count || 0}×</span>,
              last on <span className="text-gray-300 font-mono">{reboundAt}</span>
            </p>
          )}
        </div>
      )}

      <div className="surface-sunken px-4 pb-1">
        <SectionLabel hint="These are what the HWID hash is built from. Nothing removable goes in here.">
          Identity anchors
        </SectionLabel>
        <Row label="HWID (hash)"         value={user.hwid} />
        <Row label="SMBIOS UUID"         value={user.hwid_smbios_uuid} />
        <Row label="Baseboard serial"    value={user.hwid_baseboard_serial} />
        <Row label="CPU signature"       value={user.hwid_cpu_signature} />
        <Row label="System disk serial"  value={user.hwid_system_disk_serial} />

        <SectionLabel hint="Collected for recognising the machine after a real hardware change — never hashed. A plugged-in pendrive or a new network card shows up here and nowhere else.">
          Evidence only
        </SectionLabel>
        <Row label="Fixed disk serials"  value={user.hwid_fixed_disk_serials} list />
        <Row label="Machine GUID"        value={user.hwid_machine_guid} />
        <Row label="Volume serial"       value={user.hwid_volume_serial} />
        <Row label="All disk serials"    value={user.hwid_disk_serials} list />
        <Row label="MAC addresses"       value={user.hwid_mac_addresses} list />
        <Row label="CPU ID (legacy)"     value={user.hwid_cpuid} />
      </div>
    </Modal>
  );
}
